import React, { useState, useCallback, useMemo } from 'react';
import {
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import {
	LikeTwoTone,
	CommentOutlined,
	ClockCircleOutlined,
	CalendarOutlined,
	ExpandOutlined,
	EyeTwoTone,
	UserSwitchOutlined,
} from '@ant-design/icons';
import { Video } from '@/interfaces/video';
import { formatNumber } from '@/lib/helpers/formatNumber';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import WaffleChart from './WaffleChart';
import DeepInfo from './DeepInfo';
import SocialTag from './SocialTag';
import styles from './styles.module.css';

// Helper function to format date as "01/apr/2025"
const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	const day = date.getDate().toString().padStart(2, '0');
	const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 
		'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
	const month = monthNames[date.getMonth()];
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

const { Text } = Typography;

interface IProps {
	video: Video;
	searchTerm?: string;
	searchEntities?: string[];
}

const VideoCard: React.FC<IProps> = ({ video, searchTerm = '', searchEntities = [] }) => {
	const [openSentiment, setOpenSentiment] = useState<string>('');
	const [timeShow, setTimeShow] = useState(false);

	// Filter video content to only show segments containing search entities
	const filteredVideo = useMemo(() => {
		if (searchEntities.length === 0) {
			return video;
		}

		// Create search terms from entities with different formats
		const searchTerms = searchEntities.flatMap(entity => [
			entity.toLowerCase(),
			entity.toUpperCase(),
			entity,
			entity.replace(/[^a-zA-Z0-9]/g, ''), // Remove special characters
			entity.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
		]);

		// Filter content to only include segments that contain search entities
		const filteredContent = video.content?.filter(segment => {
			const text = segment.text?.toLowerCase() || '';
			return searchTerms.some(term => text.includes(term.toLowerCase()));
		}).map((segment, index) => {
			// Add mock sentiment for filtered segments
			const text = segment.text?.toLowerCase() || '';
			let sentiment = { pos: 0, neu: 1, neg: 0 };
			
			// Simple mock sentiment logic
			if (text.includes('good') || text.includes('great') || text.includes('amazing') || text.includes('love')) {
				sentiment = { pos: 0.7, neu: 0.3, neg: 0 };
			} else if (text.includes('bad') || text.includes('terrible') || text.includes('hate') || text.includes('awful')) {
				sentiment = { pos: 0, neu: 0.3, neg: 0.7 };
			} else if (index % 3 === 0) {
				// Every 3rd segment gets positive sentiment
				sentiment = { pos: 0.6, neu: 0.4, neg: 0 };
			} else if (index % 5 === 0) {
				// Every 5th segment gets negative sentiment
				sentiment = { pos: 0, neu: 0.4, neg: 0.6 };
			}
			
			return {
				...segment,
				sentiment
			};
		}) || [];

		return {
			...video,
			content: filteredContent
		};
	}, [video, searchEntities]);

	const onOpenSentiment = useCallback(() => {
		if (openSentiment === video._id) {
			return setOpenSentiment('');
		}
		return setOpenSentiment(video._id);
	}, [openSentiment, video._id]);

	const formatDuration = (duration: string): string => {
		// Convert duration to seconds if it's just a number
		const totalSeconds = parseInt(duration);
		
		if (isNaN(totalSeconds)) {
			// If it's already in "X hours, Y minutes, Z seconds" format, clean it up
			return duration.split(', ').reduce((prev, curr) => {
				if (curr.split(' ')[0] === '0') {
					return prev;
				}
				prev += curr + ' ';
				return prev;
			}, '').trim();
		}
		
		// Convert seconds to hours, minutes, seconds
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		
		const parts = [];
		if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
		if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
		if (seconds > 0) parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
		
		return parts.join(', ');
	};

	const formatYoutubeNumber = (num: number | string): string => {
		const number = typeof num === 'string' ? parseInt(num) : num;
		if (number >= 1000000) {
			return (number / 1000000).toFixed(1) + 'M';
		}
		if (number >= 1000) {
			return (number / 1000).toFixed(1) + 'K';
		}
		return number.toString();
	};

	return (
		<div className={styles.video}>
			<div className={`${styles.videoItem} ${openSentiment === video._id ? styles.expand : ''}`}>
				<div className={styles.left}>
					<img
						alt={video.metaData.title}
						src={prepareImageSrc(video.metaData.thumbnail)}
						style={{ 
							width: '100%', 
							height: '100%', 
							objectFit: 'cover',
							borderTopLeftRadius: '5px',
							borderBottomLeftRadius: openSentiment === video._id ? '0' : '5px',
						}}
					/>
				</div>
				<div className={styles.right}>
					<div className={styles.title}>
						<div>
							<a 
								target="_blank" 
								href={video.metaData.url} 
								className={styles.link}
								rel="noopener noreferrer"
							>
								<Text ellipsis style={{ maxWidth: '600px' }}>
									{video.metaData.title}
								</Text>
							</a>
						</div>
						<Tooltip
							placement="top"
							title={video.metaData.creatorName || 'Creator'}
							destroyOnHidden
						>
							<div className={styles.author}>
								<SocialTag 
									url={video.metaData.creatorUrl || ''} 
									name={video.metaData.creatorName || 'Unknown Creator'} 
								/>
							</div>
						</Tooltip>
					</div>
					
					<WaffleChart 
						video={filteredVideo} 
						timeShow={timeShow} 
						searchTerm={searchTerm}
						entities={searchEntities.length > 0 ? searchEntities : (video.metaData.entities || [])}
					/>
					
					<div className={styles.icons}>
						<div className={styles.iconsLeft}>
							<Tooltip 
								placement="top" 
								title="Subscribers"
								destroyOnHidden
							>
								<div className={styles.icon}>
									<UserSwitchOutlined style={{ color: '#f5222d' }} />
									<p>{formatYoutubeNumber(video.metaData.creatorSub || '0')}</p>
								</div>
							</Tooltip>
							<Tooltip 
								placement="top" 
								title="Views"
								destroyOnHidden
							>
								<div className={styles.icon}>
									<EyeTwoTone twoToneColor="#1677ff" />
									<p>{formatYoutubeNumber(video.metaData.stats.views)}</p>
								</div>
							</Tooltip>
						</div>
						<div className={styles.iconsRight}>
							<Tooltip 
								placement="top" 
								title="Likes"
								destroyOnHidden
							>
								<div className={styles.icon}>
									<LikeTwoTone twoToneColor="#1677ff" />
									<p>{formatYoutubeNumber(video.metaData.stats.likes)}</p>
								</div>
							</Tooltip>
							<Tooltip 
								placement="top" 
								title="Comments"
								destroyOnHidden
							>
								<div className={styles.icon}>
									<CommentOutlined style={{ color: '#1677ff' }} />
									<p>{formatYoutubeNumber(video.metaData.stats.comments)}</p>
								</div>
							</Tooltip>
							<Tooltip 
								placement="top" 
								title="Duration"
								destroyOnHidden
							>
								<div 
									className={styles.icon} 
									onClick={() => setTimeShow(!timeShow)} 
									style={{ cursor: 'pointer' }}
								>
									<ClockCircleOutlined style={{ color: '#1677ff' }} />
									<p>{formatDuration(video.metaData.duration)}</p>
								</div>
							</Tooltip>
							<Tooltip 
								placement="top" 
								title="Date published"
								destroyOnHidden
							>
								<div className={styles.icon}>
									<CalendarOutlined style={{ color: '#5a5a5a67' }} />
									<p style={{ color: '#5a5a5a67' }}>
										{formatDate(video.metaData.date)}
									</p>
								</div>
							</Tooltip>
							<div className={styles.iconLast}>
								<ExpandOutlined
									className={`${styles.sentiment} ${
										openSentiment === video._id ? styles.active : ''
									}`}
									onClick={onOpenSentiment}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<DeepInfo 
				openSentiment={openSentiment} 
				video={video}
				searchTerm={searchTerm}
			/>
		</div>
	);
};

export default VideoCard;
